import { langStore } from "@/stores/lang-store"

import AptGroupButton from "./apt-group-button"
import { aptGroupStore } from "./apt-group-store"
import { useToggleStore } from "@/hooks/use-toggle-store"
import { FAVORITE_GROUP_NAME } from "./apt-group-const"
import { toast } from "sonner"
import { getSidoByIdsAPI } from "@/apis/apt-api"
import { replaceAptsInGroupAPI } from "@/apis/apt-groups-api"
import GroupEditModeButton from "./group-edit-mode-button"
import GroupManageButton from "./group-manage-button"

const TEXT = {
  ko: {
    favorite: "즐겨찾기",
    saveError: "그룹 변경사항을 저장하지 못했습니다.",
    saveSuccess: "그룹 변경사항이 저장되었습니다."
  },
  en: {
    favorite: "Favorites",
    saveError: "Failed to save group changes.",
    saveSuccess: "Group changes have been saved."
  },
  es: {
    favorite: "Favoritos",
    saveError: "No se pudieron guardar los cambios del grupo.",
    saveSuccess: "Se han guardado los cambios del grupo."
  }
}
const AptGroupSelector = () => {
  const { lang } = langStore()

  const {
    groups,
    tempGroups,
    initTempGroups,
    setGroups,
    clearTempGroups,
    activeGroupName,
    setActiveGroupName,
    setGroupAptList
  } = aptGroupStore()

  const { open } = useToggleStore("group-manage")
  const { show: isEditMode, toggle } = useToggleStore("group-edit-mode")

  const text = TEXT[lang] ?? TEXT.ko

  const toggleEdit = async () => {
    // 편집 시작
    if (!isEditMode) {
      initTempGroups()
      setActiveGroupName(null)
      toggle()
      return
    }

    // 편집 완료: 원본과 비교해 변경된 그룹만 저장
    const changedGroups = tempGroups.filter((tempGroup) => {
      if (tempGroup.group_name === FAVORITE_GROUP_NAME) return false

      const originalGroup = groups.find((group) => group.id === tempGroup.id)

      if (!originalGroup) return false

      const originalCodes = [...(originalGroup.codes ?? [])].sort()
      const tempCodes = [...(tempGroup.codes ?? [])].sort()

      return (
        originalCodes.length !== tempCodes.length ||
        originalCodes.some((code, index) => code !== tempCodes[index])
      )
    })

    try {
      for (const group of changedGroups) {
        await replaceAptsInGroupAPI({
          group_id: group.id,
          codes: group.codes
        })
      }

      if (changedGroups.length > 0) {
        setGroups(
          groups.map((group) => {
            const updatedGroup = tempGroups.find((tempGroup) => tempGroup.id === group.id)

            return updatedGroup ? { ...group, codes: [...updatedGroup.codes] } : group
          })
        )

        toast.success(text.saveSuccess)
      }

      clearTempGroups()
      setActiveGroupName(null)
      toggle()
    } catch {
      toast.error(text.saveError)
    }
  }

  const handleGroupSelect = async (groupName) => {
    if (activeGroupName === groupName) {
      setActiveGroupName(null)
      return
    }

    if (isEditMode) {
      if (!groupName || groupName === FAVORITE_GROUP_NAME) return

      const group = tempGroups.find((item) => item.group_name === groupName)

      if (!group) {
        toast.warning("그룹 정보를 찾을 수 없습니다.")
        return
      }

      setActiveGroupName(groupName)
      return
    }

    const group =
      groupName === FAVORITE_GROUP_NAME
        ? groups.find((item) => item.group_name === FAVORITE_GROUP_NAME)
        : groups.find((item) => item.group_name === groupName)

    try {
      const codes = group?.codes ?? []
      const result = codes.length > 0 ? await getSidoByIdsAPI(codes) : []

      setGroupAptList(result)
      setActiveGroupName(groupName)
    } catch {
      toast.error("그룹 아파트 목록을 불러오지 못했습니다.")
    }
  }

  const displayGroups = isEditMode ? tempGroups : groups

  return (
    <div className="mb-4 flex min-w-0 items-center gap-2">
      <div className="flex min-w-0 flex-1 items-center gap-2 overflow-x-auto py-1">
        <AptGroupButton
          label={text.favorite}
          colorKey="yellow"
          isActive={activeGroupName === FAVORITE_GROUP_NAME}
          isEditMode={isEditMode}
          isFavorite
          onClick={() => handleGroupSelect(FAVORITE_GROUP_NAME)}
        />

        {displayGroups
          .filter((group) => group.group_name !== FAVORITE_GROUP_NAME)
          .map((group) => (
            <AptGroupButton
              key={group.id}
              label={group.group_name}
              isActive={activeGroupName === group.group_name}
              isEditMode={isEditMode}
              colorKey={group.color_key}
              onClick={() => handleGroupSelect(group.group_name)}
            />
          ))}
      </div>

      <div className="flex shrink-0 items-center gap-1.5">
        <GroupEditModeButton isEditMode={isEditMode} onClick={toggleEdit} />

        <GroupManageButton onClick={open} />
      </div>
    </div>
  )
}

export default AptGroupSelector
